import { useKeyPress } from '@smartproxy-web/shared/hooks';
import { type Variants } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';
import tailwindConfig from 'tailwind.config';

import useHash from '@/hooks/useHash';
import { cn } from '@/lib/utils';

import { NextImageProps } from '@/decodo/components/atoms/Image';
import { LinkButtonStrapiProps } from '@/decodo/components/atoms/LinkButton/LinkStrapi';
import { getWrapperMaxWidth } from '@/features/utils/utils';
import { isomorphicLocation } from '@/utils/location';

import AnimatedEl from '@/lib/motion';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { ExtraLinks } from './ExtraLinks';
import { HamburgerButton } from './HamburgerButton';
import { Logo } from './Logo';
import { MainLinkItemProps } from './MainLinkItem';
import { MainLinks } from './MainLinks';
import { MobileExtraLinks } from './MobileExtraLinks';

type NavigationProps = {
  logoImage?: NextImageProps;
  logoUrl: string;
  mainLinks: MainLinkItemProps['link'][];
  extraLinks: LinkButtonStrapiProps[];
  isRegionVisible: boolean;
};

export function Navigation({
  logoImage,
  logoUrl,
  mainLinks,
  extraLinks,
  isRegionVisible,
}: NavigationProps) {
  const [activeLinkTitle, setActiveLinkTitle] = useState('');
  const [hoveredLinkTitle, setHoveredLinkTitle] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { hash } = useHash();

  const handleLinkClick = (
    params: { title: string } | { url: string; keepMenuOpen?: boolean }
  ) => {
    if ('title' in params) {
      setActiveLinkTitle(params.title);
    }

    if ('url' in params) {
      const { keepMenuOpen = false } = params;

      if (!keepMenuOpen) {
        setIsMenuOpen(false);
      }
    }
  };

  useKeyPress('Escape', () => {
    setIsMenuOpen(false);
    handleLinkClick({ title: '' });
  });

  const handleLinkHover = (title?: string) => {
    setHoveredLinkTitle(title ?? '');
  };
  const handleLinkHoverCancel = () => {
    setHoveredLinkTitle('');
  };

  const isWidthBelowXL = useMediaQuery(
    `(max-width: ${parseInt(tailwindConfig.theme.screens.headerLg) - 1}px)`,
    (matches) => {
      if (!matches) setIsMenuOpen(false);
    }
  );

  const toggleMenuOpen = (open: boolean) => {
    setActiveLinkTitle('');
    setIsMenuOpen(open);
  };

  const [updatedMainLinks, updatedExtraLinks] = useMemo(() => {
    const hasMainLinks = mainLinks.length > 0;
    const hasExtraLinks = extraLinks.length > 0;

    const isCareersPage = isomorphicLocation()?.pathname.startsWith('/careers');
    const isDealsPage = isomorphicLocation()?.pathname.startsWith('/deals');
    const isLPPage = isomorphicLocation()?.pathname.startsWith('/lp');

    // Promote extra links to main links on careers page for mobile
    if (isCareersPage && !hasMainLinks && hasExtraLinks && isWidthBelowXL) {
      const updatedMainLinks: MainLinkItemProps['link'][] = [];
      const updatedExtraLinks: NavigationProps['extraLinks'] = [];

      return [updatedMainLinks, updatedExtraLinks];
    }

    // Show no drawer on LP and Deals page for mobile
    if (
      (isDealsPage || isLPPage) &&
      !hasMainLinks &&
      hasExtraLinks &&
      isWidthBelowXL
    ) {
      const updatedMainLinks: NavigationProps['mainLinks'] = [];
      const updatedExtraLinks: NavigationProps['extraLinks'] = [];

      return [updatedMainLinks, updatedExtraLinks];
    }

    return [mainLinks, extraLinks];
  }, [mainLinks, extraLinks, isWidthBelowXL]);

  useEffect(() => {
    // TODO: fix reliably so clicks outside of the
    // current header item close the menu
    if (hash === 'contact-sales') {
      toggleMenuOpen(false);
    }
  }, [hash]);

  return (
    <AnimatedEl
      el='nav'
      className={cn(
        getWrapperMaxWidth('mid'),
        'group/nav headerLg:min-h-[4.5rem] flex min-h-8 grow items-center justify-between gap-x-4'
      )}
      id='nav'
      data-state={isMenuOpen ? 'open' : 'closed'}
      key={String(isWidthBelowXL)}
      animate={isWidthBelowXL ? (isMenuOpen ? 'open' : 'closed') : undefined}
    >
      {!!logoImage && <Logo image={logoImage} url={logoUrl} />}
      {(updatedMainLinks.length > 0 || updatedExtraLinks.length > 0) && (
        <div className='headerLg:hidden shrink-0'>
          <HamburgerButton isOpen={isMenuOpen} onOpenChange={toggleMenuOpen} />
        </div>
      )}
      <AnimatedEl
        el='div'
        className='headerLg:static headerLg:contents headerLg:bg-transparent headerLg:p-0 bg-base-dark-white fixed bottom-0 left-full top-16 grid w-full grid-cols-1 grid-rows-2 transition-transform duration-300'
        variants={navVariants}
      >
        <MainLinks
          links={updatedMainLinks}
          activeLinkTitle={activeLinkTitle}
          onLinkClick={handleLinkClick}
          hoveredLinkTitle={!isWidthBelowXL ? hoveredLinkTitle : ''}
          onLinkHover={handleLinkHover}
          onLinkHoverCancel={handleLinkHoverCancel}
          isRegionVisible={isRegionVisible}
        />
        <ExtraLinks
          links={updatedExtraLinks}
          showRegionToggle={isRegionVisible}
          hoveredLinkTitle={!isWidthBelowXL ? hoveredLinkTitle : ''}
          onLinkHover={handleLinkHover}
          onLinkHoverCancel={handleLinkHoverCancel}
        />
        {updatedExtraLinks.length > 0 && (
          <MobileExtraLinks
            links={updatedExtraLinks}
            onLinkClick={() => toggleMenuOpen(false)}
          />
        )}
      </AnimatedEl>
    </AnimatedEl>
  );
}

const navVariants = {
  open: {
    x: '-100%',
    transition: {
      duration: 0.3,
      ease: 'easeOut',
      delayChildren: 0.35,
    },
  },
  closed: { x: 0 },
} satisfies Variants;
